import React, { useState, useEffect, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'

import {
  Box,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  useMediaQuery,
} from '@material-ui/core'

import BannerHeadCard from '../BannerHeadCard'
import {
  BannerPreview,
  PositionCustomization,
  PositionPreview,
  PrivacyPortalCustomization,
  TextCustomization,
  TextCustomizationLevel2,
} from '../BannerCustomizationCard/components'

import useCookieBanner from 'hooks/useCookieBanner'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import styles from './styles'
import theme from 'theme'

const BannerLayoutCard = () => {
  const { handleSubmit } = useFormContext()
  const snackbar = useSnackbar()

  const {
    banner,
    refreshBanner,
    nextStep,
    setIsSubmitting,
    setStepSubmit,
    bannerTextsOverrides,
  } = useCookieBanner()

  const [openBanner, setOpenBanner] = useState(true)
  const [selectedTab, setSelectedTab] = useState(
    constants.cookies.banner.TEXT_CUSTOMIZATION_CARD_ID,
  )

  const useStyles = styles({ selectedTab })
  const classes = useStyles()

  const smDown = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  })
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const positionPreview =
    selectedTab === constants.cookies.banner.POSITION_CUSTOMIZATION_CARD_ID

  const isTextCustomizationLevel2Preview =
    selectedTab === constants.cookies.banner.TEXT_CUSTOMIZATION_LEVEL_2_CARD_ID

  const handleTabChange = (_, clickedTab) => setSelectedTab(clickedTab)

  const handleFormSubmit = useCallback(
    async (data) => {
      try {
        setIsSubmitting(true)

        const bannerData = {
          position: data.position,
          hideOnAccept: data.hideOnAccept,
          hideRejectButton: data.hideRejectButton,
          privacyPortalEnabled: data.privacyPortalEnabled,
          privacyPortalUrl: data.privacyPortalUrl,
        }

        if (!isEmpty(bannerTextsOverrides)) {
          await service.cookies.bannerTextOverride.manage({
            bannerId: banner?.id,
            bannerTextsOverrides,
          })
        }

        await service.cookies.banner.updateLayout({
          bannerId: banner?.id,
          banner: bannerData,
        })

        snackbar.open({
          variant: 'success',
          message:
            constants.cookies.banner.SUCCESS_MESSAGES[
              constants.cookies.banner.STEPS.BANNER_LAYOUT
            ],
        })

        await refreshBanner()
        nextStep()
      } catch (error) {
        snackbar.open({
          variant: 'error',
          message:
            helpers.formatters.errorMessage(
              error?.response?.data?.error,
              false,
            ) || 'Erro ao salvar layout do banner. Tente novamente.',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      banner,
      bannerTextsOverrides,
      setIsSubmitting,
      snackbar,
      refreshBanner,
      nextStep,
    ],
  )

  useEffect(() => {
    if (!banner) return
    setStepSubmit(handleSubmit(handleFormSubmit))
  }, [banner, handleSubmit, handleFormSubmit, setStepSubmit])

  const leftSizes = helpers.cookies.banner.computeLeftGridSizes({
    positionPreview,
    isTextCustomizationLevel2Preview,
    logoPreview: false,
  })
  const rightSizes = helpers.cookies.banner.computeRightGridSizes({
    positionPreview,
    isTextCustomizationLevel2Preview,
    logoPreview: false,
  })

  return (
    <Paper
      variant="outlined"
      id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_8}
    >
      <BannerHeadCard name="Layout do Banner" />
      <Divider />
      <Box
        p={2}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(2)}
      >
        <Tabs
          value={selectedTab}
          variant="scrollable"
          onChange={handleTabChange}
        >
          {constants.cookies.banner.BANNER_LAYOUT_CARD_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.tabName} value={tab.id} />
          ))}
        </Tabs>
        <Grid container spacing={smDown ? 2 : 0}>
          <Grid item {...leftSizes}>
            <Box className={classes.textCustomizationContainer}>
              <Box
                display="flex"
                width="100%"
                flexDirection="column"
                alignItems="flex-start"
              >
                <TextCustomization />
              </Box>
              {mdUp && <Divider orientation="vertical" variant="middle" />}
            </Box>
            <Box className={classes.textCustomizationLevel2Container}>
              <Box
                display="flex"
                width="100%"
                flexDirection="column"
                alignItems="flex-start"
              >
                <TextCustomizationLevel2 />
              </Box>
            </Box>
            <Box className={classes.positionCustomizationContainer}>
              <PositionCustomization />
            </Box>
            <Box className={classes.privacyPortalCustomizationContainer}>
              <PrivacyPortalCustomization />
            </Box>
          </Grid>
          <Grid item {...rightSizes}>
            <Box className={classes.bannerPreviewContainer}>
              <BannerPreview
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
                showSecondLevelInlinePreview={isTextCustomizationLevel2Preview}
              />
            </Box>
            <Box className={classes.positionPreviewContainer}>
              <PositionPreview />
            </Box>
            <Box className={classes.privacyPortalPreviewContainer}>
              <BannerPreview
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default BannerLayoutCard
